<template>
  <v-container class="pricing-page py-10">
    <div class="text-center mb-8">
      <h1 class="text-h3 mb-3">
        More room for your story
      </h1>
      <p class="text-body-1">
        Start free. Choose more storage and AI credits when you need them.
      </p>
      <v-btn
        v-if="$store.state.logged"
        text
        color="primary"
        to="/billing"
      >
        My subscription
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="info"
      outlined
    >
      {{ error }}
      <v-btn
        text
        small
        :loading="loading"
        @click="loadCatalog"
      >
        Try again
      </v-btn>
    </v-alert>
    <v-alert
      v-if="catalog && catalog.checkoutEnabled && catalog.mode === 'test'"
      type="info"
      outlined
    >
      Payment testing is in progress. These purchases use Stripe test mode.
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
      aria-label="Loading plans"
    ></v-progress-linear>
    <v-row v-if="catalog">
      <v-col
        v-for="plan in catalog.plans"
        :key="plan.id"
        cols="12"
        md="4"
      >
        <v-card
          outlined
          height="100%"
          class="pa-6 d-flex flex-column"
          :class="{ 'featured-plan': plan.id === 'personal' }"
        >
          <h2 class="text-h5 mb-4">
            {{ plan.name }}
          </h2>
          <p><strong class="text-h3">{{ money(plan.amount) }}</strong><span v-if="plan.amount"> / month</span></p>
          <ul class="plan-benefits mb-6">
            <li>{{ storage(plan.storageBytes) }} of storage</li>
            <li v-if="plan.creditPeriod === 'welcome'">
              {{ plan.credits }} one-time welcome AI credits, valid for 30 days
            </li>
            <li v-else>
              {{ plan.credits }} AI credits each billing cycle
            </li>
            <li>Photos, biography, and diary</li>
            <li>Keep control of your privacy</li>
          </ul>
          <v-spacer></v-spacer>
          <v-btn
            :color="plan.id === 'personal' ? 'primary' : undefined"
            :outlined="plan.id !== 'personal'"
            :disabled="plan.amount > 0 && !catalog.checkoutEnabled"
            :to="destination(plan)"
            block
          >
            {{ plan.amount === 0 ? 'Start free' : catalog.checkoutEnabled ? `Choose ${plan.name}` : 'Coming soon' }}
          </v-btn>
        </v-card>
      </v-col>
    </v-row>
    <v-card
      v-if="catalog"
      outlined
      class="pa-6 mt-6"
    >
      <h2 class="text-h5 mb-3">
        Need more AI credits?
      </h2>
      <p>{{ catalog.aiPack.credits }} additional credits for {{ money(catalog.aiPack.amount) }}. One-time purchase. No expiration.</p>
      <v-btn
        color="primary"
        outlined
        :disabled="!catalog.checkoutEnabled"
        :to="packDestination"
      >
        View AI pack
      </v-btn>
    </v-card>
    <div class="mt-8 text-body-2">
      <p>Monthly credits do not roll over. Purchased credits remain separate from your subscription.</p>
      <p>Need more storage? Choose a larger plan. There are no separate storage packs.</p>
      <p>Cancel at the end of your billing period. Your photos and stories are not automatically deleted when you cancel.</p>
    </div>
  </v-container>
</template>

<script>
import billing from '@/services/billing'

export default {
  name: 'BillingPlans',
  data: () => ({ catalog: null, loading: false, error: '' }),
  computed: {
    packDestination() {
      return this.$store.state.logged ? '/billing?offer=ai100' : '/auth'
    },
  },
  mounted() { this.loadCatalog() },
  methods: {
    money(amount) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount / 100) },
    storage(bytes) { return bytes < 1000000000 ? `${bytes / 1000000} MB` : `${bytes / 1000000000} GB` },
    destination(plan) {
      if (!this.$store.state.logged) return '/auth'

      return plan.id === 'free' ? '/my-pictures' : `/billing?offer=${plan.id}`
    },
    async loadCatalog() {
      this.loading = true
      this.error = ''
      try { this.catalog = await billing.catalog() } catch (_) {
        this.error = 'Plans are being prepared. Your existing account remains available.'
      } finally { this.loading = false }
    },
  },
}
</script>

<style scoped>
.pricing-page { max-width: 1120px; }
.plan-benefits { line-height: 2; padding-left: 20px; }
.featured-plan { border: 2px solid var(--v-primary-base) !important; }
</style>
